// src/pages/bookings/hooks/useAdminBookingsPage.ts

import { useEffect, useMemo, useState } from "react";

import {
  useAdminBookingsList,
  useAdminBookingsSearch,
  useAdminFlightTabCounts,
  useAdminHotelTabCounts,
  useAdminTourTabCounts,
} from "@/api/features/bookings/queries";

import type {
  AdminBookingResult,
  AdminListBookingsQuery,
  BookingStatus,
} from "@/api/features/bookings/types";

/* ============================================================================
   Types – UI-level shapes (for table + tabs)
============================================================================ */

export type BookingTabKey =
  | "ALL"
  | "DRAFT"
  | "ON_HOLD"
  | "WAITING_PAYMENT"
  | "PARTIALLY_PAID"
  | "CONFIRMED"
  | "CANCELLED"
  | "OTHER";

export type DatePreset =
  | "ALL"
  | "TODAY"
  | "LAST_7_DAYS"
  | "LAST_30_DAYS"
  | "CUSTOM";

export type BookingProductFilter = "TOUR" | "FLIGHT" | "HOTEL";

/** Hotel-specific columns, from the backend's per-row `hotelSummary`. */
export type HotelRowSummary = {
  hotelName: string | null;
  cityName: string | null;
  countryName: string | null;
  roomName: string | null;
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  rooms: number | null;
  guests: number;
  confirmationNo: string | null;
  supplier: string | null;
  supplierStatus: string | null;
  cancellationStatus: string | null;
  refundStatus: string | null;
};

export type AdminBookingsFilters = {
  q?: string;

  /**
   * Shopify-like: tab is primary driver for status group,
   * but we still keep status exposed for future use.
   */
  status?: BookingStatus | "ALL";

  /**
   * Date range presets + custom
   */
  datePreset?: DatePreset;
  fromDate?: string;
  toDate?: string;

  /**
   * Sorting
   */
  sortBy?: "createdAt" | "updatedAt" | "travelDate";
  orderBy?: "asc" | "desc";
};

/** Per-column funnel filters (server-side). */
export type AdminBookingsColumnFilters = {
  bookingCode?: string;
  customer?: string;
};

export type BookingRow = {
  id: number;
  bookingCode?: string | null;
  pnr?: string | null;
  status: BookingStatus;

  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;

  customerCode?: string | null; // ✅ add this

  productLabel?: string | null;
  travelStartDate?: string | null;
  travelEndDate?: string | null;

  paxLabel: string;
  totalLabel: string;
  paidTotalLabel: string;

  updatedAt?: string | null;
  createdAt?: string | null;

  /** Present on hotel bookings only. */
  hotel?: HotelRowSummary | null;
};

/* ============================================================================
   Small helpers
============================================================================ */

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

/** Return YYYY-MM-DD in local time */
function toYmd(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDays(date: Date, delta: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + delta);
  return d;
}

function clampPage(n: unknown) {
  const x = typeof n === "number" ? n : Number(n);
  return Number.isFinite(x) ? Math.max(1, Math.floor(x)) : 1;
}

function clampPerPage(n: unknown) {
  const x = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(x)) return 10;
  return Math.max(1, Math.min(100, Math.floor(x)));
}

function safeNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatMoney(amount: number | null, currencyCode?: string | null) {
  if (amount === null) return "—";
  const code = String(currencyCode || "").trim().toUpperCase() || "INR";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      maximumFractionDigits: code === "INR" ? 0 : 2,
    }).format(amount);
  } catch {
    return `${code} ${code === "INR" ? Math.round(amount) : Number(amount).toFixed(2)}`;
  }
}

function makePaxLabel(b: AdminBookingResult) {
  const p = b.paxJson;
  if (!p) return "—";

  const a = Number(p.adults ?? 0);
  const cwb = Number(p.childWb ?? 0);
  const cwob = Number(p.childWob ?? 0);
  const inf = Number(p.infants ?? 0);

  const parts: string[] = [];
  parts.push(`${a}A`);
  if (cwb) parts.push(`${cwb}CWB`);
  if (cwob) parts.push(`${cwob}CWOB`);
  if (inf) parts.push(`${inf}I`);
  return parts.join(" ");
}

function makeCustomerName(b: AdminBookingResult) {
  // ✅ new: customer summary from backend aggregate
  const display = (b as any)?.customer?.displayName?.trim();
  if (display) return display;

  const company = (b as any)?.customer?.companyName?.trim();
  if (company) return company;

  // fallback (if you ever set booking contactName)
  const contact = (b as any)?.contactName?.trim();
  if (contact) return contact;

  if (b.customerUserId != null) return `User #${b.customerUserId}`;
  return null;
}

function pickTourTitle(b: AdminBookingResult) {
  if (b.pricingSnapshot?.items && b.pricingSnapshot.items.length > 0) {
    const title = (b.pricingSnapshot.items[0]?.title || "").trim();
    if (title) return title;
  }
  return null;
}

function pickProductLabel(b: AdminBookingResult) {
  const fromSnapshot = (b.pricingSnapshot?.items ?? []).find(
    (it) => it && (it.title || it.productType)
  );

  if (fromSnapshot?.title && String(fromSnapshot.title).trim()) {
    return String(fromSnapshot.title).trim();
  }

  if (fromSnapshot?.productType && String(fromSnapshot.productType).trim()) {
    return String(fromSnapshot.productType).trim();
  }

  return pickTourTitle(b);
}

function makeTotalLabel(b: AdminBookingResult) {
  const paidCurrencyCode =
    String(
      (b as any)?.pricingSnapshot?.currency?.charge ||
        (b as any)?.pricingSnapshot?.paidCurrencyCode ||
        b.currencyCode ||
        "INR"
    )
      .trim()
      .toUpperCase() || "INR";
  const displayCurrencyCode =
    String(
      (b as any)?.pricingSnapshot?.currency?.display ||
        (b as any)?.pricingSnapshot?.displayCurrencyCode ||
        paidCurrencyCode
    )
      .trim()
      .toUpperCase() || paidCurrencyCode;
  const supplierCurrencyCode =
    String(
      (b as any)?.pricingSnapshot?.currency?.supplier ||
        (b as any)?.pricingSnapshot?.supplierCurrencyCode ||
        paidCurrencyCode
    )
      .trim()
      .toUpperCase() || paidCurrencyCode;

  const displayAmount = safeNum((b as any)?.pricingSnapshot?.displayAmount);
  if (displayAmount !== null && displayCurrencyCode) {
    return formatMoney(displayAmount, displayCurrencyCode);
  }

  const supplierTotal = safeNum((b as any)?.grandTotal);
  if (supplierTotal !== null) {
    return formatMoney(supplierTotal, supplierCurrencyCode);
  }

  const paidTotal = safeNum((b as any)?.paidTotal);
  return formatMoney(paidTotal, paidCurrencyCode);
}

function makePaidTotalLabel(b: AdminBookingResult) {
  const paidCurrencyCode =
    String(
      (b as any)?.pricingSnapshot?.currency?.charge ||
        (b as any)?.pricingSnapshot?.paidCurrencyCode ||
        b.currencyCode ||
        "INR"
    )
      .trim()
      .toUpperCase() || "INR";

  const paidTotal = safeNum((b as any)?.paidTotal);
  return formatMoney(paidTotal, paidCurrencyCode);
}

function pickBookingPnr(b: AdminBookingResult): string | null {
  const pnr =
    String(
      (b as any)?.pricingSnapshot?.pnr ||
        (b as any)?.pricingSnapshot?.itinerary?.PNR ||
        (b as any)?.pricingSnapshot?.response?.Response?.Response?.PNR ||
        (b as any)?.pricingSnapshot?.response?.Response?.Response?.FlightItinerary?.PNR ||
        (b as any)?.pricingSnapshot?.response?.Response?.FlightItinerary?.PNR ||
        ""
    )
      .trim()
      .toUpperCase() || "";
  return pnr || null;
}

function mapBookingToRow(b: AdminBookingResult): BookingRow {
  return {
    id: Number(b.id),
    bookingCode: b.bookingCode ?? null,
    pnr: pickBookingPnr(b),
    status: b.status,

    // ✅ prefer backend flat fields
    customerName:
      (b as any)?.customerName ??
      makeCustomerName(b),

    customerEmail:
      (b as any)?.customerEmail ??
      (b as any)?.contactEmail ??
      (b as any)?.customer?.email ??
      null,

    customerPhone:
      (b as any)?.customerPhone ??
      (b as any)?.contactPhone ??
      (b as any)?.customer?.phone ??
      null,

    productLabel: pickProductLabel(b),

    travelStartDate: b.travelStartDate ?? null,
    travelEndDate: b.travelEndDate ?? null,

    paxLabel: makePaxLabel(b),
    totalLabel: makeTotalLabel(b),
    paidTotalLabel: makePaidTotalLabel(b),

    updatedAt: b.updatedAt ?? null,
    createdAt: b.createdAt ?? null,

    hotel: pickHotelSummary(b),
  };
}

function pickHotelSummary(b: AdminBookingResult): HotelRowSummary | null {
  const h = (b as { hotelSummary?: Record<string, unknown> | null }).hotelSummary;
  if (!h || typeof h !== "object") return null;
  const text = (v: unknown) => (v == null || String(v).trim() === "" ? null : String(v));
  return {
    hotelName: text(h.hotelName),
    cityName: text(h.cityName),
    countryName: text(h.countryName),
    roomName: text(h.roomName),
    checkIn: text(h.checkIn),
    checkOut: text(h.checkOut),
    nights: safeNum(h.nights) ?? 0,
    rooms: safeNum(h.rooms),
    guests: safeNum(h.guests) ?? 0,
    confirmationNo: text(h.confirmationNo),
    supplier: text(h.supplier),
    supplierStatus: text(h.supplierStatus),
    cancellationStatus: text(h.cancellationStatus),
    refundStatus: text(h.refundStatus),
  };
}


/** Debounce a string value (for q search) */
function useDebouncedValue(value: string, delayMs: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);

  return debounced;
}

/* ============================================================================
   Hook: state + logic for Admin Bookings listing page
============================================================================ */

export function useAdminBookingsPage(
  pageSize: number = 10,
  forcedProductType?: BookingProductFilter
) {
  const [productType, setProductType] = useState<BookingProductFilter>(
    forcedProductType ?? "TOUR"
  );

  useEffect(() => {
    if (!forcedProductType) return;
    setProductType(forcedProductType);
  }, [forcedProductType]);

  // Tabs
  const [tab, setTab] = useState<BookingTabKey>(
    forcedProductType === "FLIGHT" ? "CONFIRMED" : "ALL"
  );

  useEffect(() => {
    if (forcedProductType !== "FLIGHT") return;
    setTab((prev) =>
      prev === "ON_HOLD" || prev === "WAITING_PAYMENT" ? "CONFIRMED" : prev
    );
  }, [forcedProductType]);

  // Search + filters
  const [q, setQ] = useState<string>("");

  // Per-column funnel filters (Booking Code, Customer)
  const [colFilters, setColFilters] = useState<AdminBookingsColumnFilters>({});

  const [filters, setFilters] = useState<AdminBookingsFilters>(() => ({
    status: "ALL",
    datePreset: "ALL",
    fromDate: "",
    toDate: "",
    sortBy: "createdAt",
    orderBy: "desc",
  }));

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(pageSize);

  const qDebounced = useDebouncedValue(q, 400);

  // Reset to first page on any effective filter change
  useEffect(() => {
    setPage(1);
  }, [
    tab,
    productType,
    qDebounced,
    filters.status,
    filters.datePreset,
    filters.fromDate,
    filters.toDate,
    filters.sortBy,
    filters.orderBy,
    colFilters.bookingCode,
    colFilters.customer,
    perPage,
  ]);

  // Date preset → from/to
  const effectiveDateRange = useMemo(() => {
    const preset = filters.datePreset ?? "ALL";
    if (preset === "ALL") return { fromDate: undefined, toDate: undefined };

    const today = new Date();
    const todayYmd = toYmd(today);

    if (preset === "TODAY") {
      return { fromDate: todayYmd, toDate: todayYmd };
    }

    if (preset === "LAST_7_DAYS") {
      return { fromDate: toYmd(addDays(today, -6)), toDate: todayYmd };
    }

    if (preset === "LAST_30_DAYS") {
      return { fromDate: toYmd(addDays(today, -29)), toDate: todayYmd };
    }

    // CUSTOM
    const from = (filters.fromDate || "").trim();
    const to = (filters.toDate || "").trim();
    return { fromDate: from || undefined, toDate: to || undefined };
  }, [filters.datePreset, filters.fromDate, filters.toDate]);

  // Tab → status
  const statusFromTab: BookingStatus | undefined = useMemo(() => {
    if (tab === "ALL") return undefined;
    if (tab === "DRAFT") return "DRAFT";
    if (tab === "ON_HOLD") return "ON_HOLD";
    if (tab === "WAITING_PAYMENT") return "WAITING_PAYMENT";
    if (tab === "PARTIALLY_PAID") return "PARTIALLY_PAID";
    if (tab === "CONFIRMED") return "CONFIRMED";
    if (tab === "CANCELLED") return "CANCELLED";
    return undefined; // OTHER
  }, [tab]);

  // Build API query
  const apiQuery: AdminListBookingsQuery = useMemo(() => {
    return {
      page: clampPage(page),
      perPage: clampPerPage(perPage),

      q: qDebounced.trim() || undefined,
      bookingCode: colFilters.bookingCode?.trim() || undefined,
      customer: colFilters.customer?.trim() || undefined,
      status: statusFromTab,
      itemType: productType,

      fromDate: effectiveDateRange.fromDate,
      toDate: effectiveDateRange.toDate,

      sortBy: filters.sortBy ?? "createdAt",
      orderBy: filters.orderBy ?? "desc",
    };
  }, [
    page,
    perPage,
    qDebounced,
    colFilters.bookingCode,
    colFilters.customer,
    statusFromTab,
    productType,
    effectiveDateRange.fromDate,
    effectiveDateRange.toDate,
    filters.sortBy,
    filters.orderBy,
  ]);

  // Fetch: list vs search
  const shouldUseSearch = (apiQuery.q || "").trim().length > 0;

  const listQuery = useAdminBookingsList(
    shouldUseSearch ? { ...apiQuery, q: undefined } : apiQuery
  );

  const searchQuery = useAdminBookingsSearch(
    shouldUseSearch ? apiQuery : { ...apiQuery, q: undefined }
  );

  const activeQuery = shouldUseSearch ? searchQuery : listQuery;
  const tourTabCountsQuery = useAdminTourTabCounts(productType === "TOUR");
  const flightTabCountsQuery = useAdminFlightTabCounts(productType === "FLIGHT");
  const hotelTabCountsQuery = useAdminHotelTabCounts(productType === "HOTEL");

  const apiItems: AdminBookingResult[] = activeQuery.data?.items ?? [];

  // OTHER tab client-side grouping (if backend already filtered by statusFromTab undefined)
  const otherExcluded = useMemo(
    () =>
      new Set<BookingStatus>([
        "DRAFT",
        "ON_HOLD",
        "WAITING_PAYMENT",
        "PARTIALLY_PAID",
        "CONFIRMED",
      ]),
    []
  );

  const filteredItems: AdminBookingResult[] = useMemo(() => {
    if (tab !== "OTHER") return apiItems;
    return apiItems.filter((b) => !otherExcluded.has(b.status));
  }, [tab, apiItems, otherExcluded]);

  // Map to UI rows
  const rows: BookingRow[] = useMemo(
    () => filteredItems.map(mapBookingToRow),
    [filteredItems]
  );

  // Paging meta
  const totalPages = Math.max(1, Number(activeQuery.data?.totalPages ?? 1));
  const totalItems = Number(activeQuery.data?.total ?? 0);
  const serialStart = (Math.max(1, page) - 1) * Math.max(1, perPage);

  const tabCounts = useMemo(() => {
    const countsSource =
      productType === "FLIGHT"
        ? flightTabCountsQuery.data
        : productType === "HOTEL"
        ? hotelTabCountsQuery.data
        : tourTabCountsQuery.data;
    const apiCounts = countsSource?.counts ?? {};

    const otherCount =
      Number(apiCounts.PAID ?? 0) +
      Number(apiCounts.CANCELLED ?? 0) +
      Number(apiCounts.EXPIRED ?? 0) +
      Number(apiCounts.REFUNDED ?? 0);

    return {
      ALL: Number(apiCounts.ALL ?? countsSource?.total ?? 0),
      DRAFT: Number(apiCounts.DRAFT ?? 0),
      ON_HOLD: Number(apiCounts.ON_HOLD ?? 0),
      WAITING_PAYMENT: Number(apiCounts.WAITING_PAYMENT ?? 0),
      PARTIALLY_PAID: Number(apiCounts.PARTIALLY_PAID ?? 0),
      CONFIRMED: Number(apiCounts.CONFIRMED ?? 0),
      CANCELLED: Number(apiCounts.CANCELLED ?? 0),
      OTHER: otherCount,
    } as Record<BookingTabKey, number>;
  }, [flightTabCountsQuery.data, hotelTabCountsQuery.data, productType, tourTabCountsQuery.data]);

  return {
    tab,
    productType,
    setProductType,
    setTab,

    q,
    setQ,
    filters,
    setFilters,
    colFilters,
    setColFilters,

    page,
    setPage,
    perPage,
    setPerPage,
    totalPages,
    totalItems,
    serialStart,

    rows,
    tabCounts,

    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    refetch: () => activeQuery.refetch(),
  };
}
